import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material';

interface FilenameTableProps {
  e53FilenamesByQuarter: Record<string, string>;
  e55FilenamesByQuarter: Record<string, string>;
  ppo: string;
}

const mapQuarterToDisplay = (quarter: string): string => {
  switch (quarter) {
    case 'ONE': return 'Q1';
    case 'TWO': return 'Q2';
    case 'THREE': return 'Q3';
    case 'FOUR': return 'Q4';
    default: return quarter;
  }
};

function FilenameTable({ e53FilenamesByQuarter, e55FilenamesByQuarter, ppo }: FilenameTableProps) {
  // Get all quarters from both e53 and e55 data
  const allQuarters = new Set([
    ...Object.keys(e53FilenamesByQuarter || {}),
    ...Object.keys(e55FilenamesByQuarter || {})
  ]);
  
  const quarters = Array.from(allQuarters).sort((a, b) => {
    const order = { 'ONE': 1, 'TWO': 2, 'THREE': 3, 'FOUR': 4 };
    return (order[a as keyof typeof order] || 99) - (order[b as keyof typeof order] || 99);
  });

  // Don't render the table if there are no quarters with data
  if (quarters.length === 0) {
    return <Box data-testid="filename-table-empty">No filename data available</Box>;
  }

  return (
    <Box sx={{ mb: 4 }} data-testid="filename-table-container">
      <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }} data-testid="filename-table-title">
        File Names - {ppo}
      </Typography>
      
      <TableContainer component={Paper} sx={{ maxWidth: '100%' }} data-testid="filename-table-container-paper">
        <Table size="small" data-testid="filename-table">
          <TableHead data-testid="filename-table-head">
            <TableRow sx={{ backgroundColor: '#E7EFF5' }} data-testid="filename-table-header-row">
              <TableCell sx={{ fontWeight: 600, color: '#063142' }} data-testid="filename-table-quarter-header">Quarter</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#063142' }} data-testid="filename-table-e53-header">e53 Filename</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#063142' }} data-testid="filename-table-e55-header">e55 Filename</TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="filename-table-body">
            {quarters.map((quarter) => (
              <TableRow key={quarter} hover data-testid={`filename-table-row-${quarter}`}>
                <TableCell sx={{ fontWeight: 500 }} data-testid={`filename-table-quarter-cell-${quarter}`}>
                  {mapQuarterToDisplay(quarter)}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }} data-testid={`filename-table-e53-cell-${quarter}`}>
                  {e53FilenamesByQuarter?.[quarter] || '-'}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }} data-testid={`filename-table-e55-cell-${quarter}`}>
                  {e55FilenamesByQuarter?.[quarter] || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default FilenameTable;
